import React, { useEffect, useRef } from 'react';
import { BetaSignup, DashboardStats } from '../../types';

interface ChartsProps {
  signups: BetaSignup[];
  stats: DashboardStats;
}

const Charts: React.FC<ChartsProps> = ({ signups, stats }) => {
  const pieChartRef = useRef<SVGGElement>(null);
  const trendChartRef = useRef<HTMLDivElement>(null);
  const experienceChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    createStatusPieChart();
    createSignupTrendChart();
    createExperienceBarChart();
  }, [signups, stats]); // eslint-disable-line react-hooks/exhaustive-deps

  const createStatusPieChart = () => {
    if (!pieChartRef.current) return;

    const statusData = [
      { status: 'approved', count: stats.approved || 0, color: '#10b981', label: 'Approved' },
      { status: 'pending', count: stats.pending || 0, color: '#f59e0b', label: 'Pending' },
      { status: 'rejected', count: stats.rejected || 0, color: '#ef4444', label: 'Rejected' },
      { status: 'invited', count: stats.invited || 0, color: '#3b82f6', label: 'Invited' }
    ].filter(item => item.count > 0);

    const total = statusData.reduce((sum, item) => sum + item.count, 0);
    if (total === 0) return;

    let cumulativeAngle = 0;

    // Clear previous content
    pieChartRef.current.innerHTML = '';

    statusData.forEach((item) => {
      const percentage = item.count / total;
      const angle = percentage * 360;

      if (percentage > 0) {
        // Create pie slice
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const startAngle = cumulativeAngle;
        const endAngle = cumulativeAngle + angle;

        const x1 = 80 * Math.cos((startAngle * Math.PI) / 180);
        const y1 = 80 * Math.sin((startAngle * Math.PI) / 180);
        const x2 = 80 * Math.cos((endAngle * Math.PI) / 180);
        const y2 = 80 * Math.sin((endAngle * Math.PI) / 180);

        const largeArcFlag = angle > 180 ? 1 : 0;
        const pathData = [
          'M', 0, 0,
          'L', x1, y1,
          'A', 80, 80, 0, largeArcFlag, 1, x2, y2,
          'Z'
        ].join(' ');

        path.setAttribute('d', pathData);
        path.setAttribute('fill', item.color);
        path.classList.add('pie-slice');
        path.setAttribute('data-status', item.status);

        pieChartRef.current!.appendChild(path);
        cumulativeAngle += angle;
      }
    });
  };

  const createSignupTrendChart = () => {
    if (!trendChartRef.current) return;
    
    const last30Days = [];

    // Get last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = signups.filter(signup => {
        if (!signup.timestamp) return false;
        try {
          const signupDate = new Date(signup.timestamp);
          const signupDateStr = signupDate.toISOString().split('T')[0];
          return signupDateStr === dateStr;
        } catch (e) {
          return false;
        }
      }).length;
      last30Days.push({ date: dateStr, count });
    }

    const maxCount = Math.max(...last30Days.map(d => d.count));
    const chartHeight = 140;
    const chartWidth = Math.max(trendChartRef.current.offsetWidth - 40, 300);

    // Clear previous content
    trendChartRef.current.innerHTML = '';

    // Create SVG
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', chartWidth.toString());
    svg.setAttribute('height', chartHeight.toString());
    svg.style.position = 'absolute';
    svg.style.top = '10px';
    svg.style.left = '20px';

    // Create path for the line
    const pathData = last30Days.map((point, index) => {
      const x = (index / (last30Days.length - 1)) * chartWidth;
      const y = chartHeight - (point.count / Math.max(maxCount, 1)) * chartHeight;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathData);
    path.classList.add('chart-line');
    svg.appendChild(path);

    // Add data points
    last30Days.forEach((point, index) => {
      if (point.count > 0) {
        const x = (index / (last30Days.length - 1)) * chartWidth;
        const y = chartHeight - (point.count / Math.max(maxCount, 1)) * chartHeight;

        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', x.toString());
        circle.setAttribute('cy', y.toString());
        circle.setAttribute('r', '4');
        circle.classList.add('chart-point');
        svg.appendChild(circle);
      }
    });

    trendChartRef.current.appendChild(svg);

    // Add axis labels
    const xAxis = document.createElement('div');
    xAxis.className = 'chart-axis x-axis';
    xAxis.innerHTML = `
      <span>${last30Days[0].date.split('-').slice(1).join('/')}</span>
      <span>${last30Days[Math.floor(last30Days.length/2)].date.split('-').slice(1).join('/')}</span>
      <span>${last30Days[last30Days.length-1].date.split('-').slice(1).join('/')}</span>
    `;
    trendChartRef.current.appendChild(xAxis);

    const yAxis = document.createElement('div');
    yAxis.className = 'chart-axis y-axis';
    yAxis.innerHTML = `
      <span>${maxCount}</span>
      <span>${Math.floor(maxCount/2)}</span>
      <span>0</span>
    `;
    trendChartRef.current.appendChild(yAxis);
  };

  const createExperienceBarChart = () => {
    if (!experienceChartRef.current) return;

    // Group by experience
    const experienceGroups = {
      '0-1': { label: 'Beginner', count: 0, color: '#3b82f6' },
      '2-5': { label: 'Intermediate', count: 0, color: '#10b981' },
      '6-10': { label: 'Advanced', count: 0, color: '#f59e0b' },
      '10+': { label: 'Expert', count: 0, color: '#ef4444' }
    };

    signups.forEach(signup => {
      const experience = signup.experienceYears;
      if (experience && experienceGroups[experience as keyof typeof experienceGroups]) {
        experienceGroups[experience as keyof typeof experienceGroups].count++;
      }
    });

    const maxCount = Math.max(...Object.values(experienceGroups).map(g => g.count));
    const chartHeight = 140;

    // Clear previous content
    experienceChartRef.current.innerHTML = '';

    Object.entries(experienceGroups).forEach(([key, group]) => {
      const barItem = document.createElement('div');
      barItem.className = 'bar-item';

      const height = maxCount > 0 ? (group.count / maxCount) * chartHeight : 0;

      barItem.innerHTML = `
        <div class="bar" style="height: ${height}px; background: linear-gradient(135deg, ${group.color}, ${group.color}dd);">
          <div class="bar-value">${group.count}</div>
        </div>
        <div class="bar-label">${group.label}</div>
      `;

      experienceChartRef.current!.appendChild(barItem);
    });
  };

  return (
    <div className="charts-grid">
      {/* Status Distribution Chart */}
      <div className="chart-card">
        <div className="card-header">
          <h3 className="card-title">
            <span>📊</span>
            Status Distribution
          </h3>
          <p className="card-description">Current signup status breakdown</p>
        </div>
        <div className="chart-container">
          <div className="pie-chart">
            <svg viewBox="0 0 200 200" className="pie-chart-svg">
              <g transform="translate(100, 100)" ref={pieChartRef}>
                {/* Pie slices will be populated by JavaScript */}
              </g>
            </svg>
          </div>
        </div>
      </div>

      {/* Signup Trend Chart */}
      <div className="chart-card">
        <div className="card-header">
          <h3 className="card-title">
            <span>📈</span>
            Signup Trends
          </h3>
          <p className="card-description">Daily signup activity over time</p>
        </div>
        <div className="chart-container">
          <div className="line-chart" ref={trendChartRef}>
            {/* Trend chart will be populated by JavaScript */}
          </div>
        </div>
      </div>

      {/* Experience Level Chart */}
      <div className="chart-card">
        <div className="card-header">
          <h3 className="card-title">
            <span>🎯</span>
            Experience Levels
          </h3>
          <p className="card-description">Beta tester experience distribution</p>
        </div>
        <div className="chart-container">
          <div className="bar-chart" ref={experienceChartRef}>
            {/* Experience chart will be populated by JavaScript */}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="chart-card">
        <div className="card-header">
          <h3 className="card-title">
            <span>⚡</span>
            Quick Actions
          </h3>
          <p className="card-description">Common administrative tasks</p>
        </div>
        <div className="quick-actions">
          <button className="btn btn-success">
            <span>🚀</span>
            Send TestFlight Invites
          </button>
          <button className="btn btn-primary">
            <span>📥</span>
            Export Data
          </button>
          <button className="btn btn-warning">
            <span>📱</span>
            Update Version
          </button>
        </div>
      </div>
    </div>
  );
};

export default Charts;
